import { WorkApproval } from './WorkApproval';
import { Conversation } from '../../support/Conversation';
import React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import type { Task, TaskPriority, TaskStatus } from '../../../core/models/Task';
import { UserRole } from '../../../core/models/User';
import { compareTaskVersions, formatTaskVersion, parseTaskVersion } from '../../../core/constants/taskVersionMilestone';
import { useMemo } from 'react';
import { useGithub, type GithubBranch, type GithubIssue } from '../../../core/hooks/useGithub';
import { Check, Copy } from 'lucide-react';

/** Shown for every project; persisted on the project when you add a custom label. */
const DEFAULT_TASK_LABELS = ['bug', 'feature'] as const;

function normalizeTaskLabel(raw: string): string | null {
  const t = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!t || t.length > 80) return null;
  return t;
}

interface TaskDialogProps {
  open: boolean;
  onClose: () => void;
  task?: Partial<Task>;
  projects: Array<{
    id: string;
    name: string;
    clientId: string;
    jiraProjectId?: string;
    taskFieldVisibility?: {
      github?: boolean;
      jira?: boolean;
    };
    taskVersions?: string[];
    platforms?: Array<{ id: string; name: string }>;
    taskLabels?: string[];
    githubRepoId?: number;
    githubRepoOwner?: string;
    githubRepoName?: string;
  }>;
  clientAccounts?: Array<{ id: string; name: string }>;
  users: Array<{
    uid: string;
    email: string | null;
    displayName?: string | null;
    firstName?: string;
    lastName?: string;
    role?: string;
    clientId?: string;
    organizationId?: string;
    projectAccess?: Record<string, { role: string }>;
    clientAccess?: Record<string, { role: string }>;
  }>;
  isSuperAdmin?: boolean;
  fixedClientId?: string | null;
  onAddProjectVersion?: (projectId: string, version: string) => Promise<void>;
  onAddProjectLabel?: (projectId: string, label: string) => Promise<void>;
  actualMinutes?: number;
  onSubmit: (data: any) => Promise<void>;
}

export const TaskDialog: React.FC<TaskDialogProps> = ({
  open,
  onClose,
  task,
  projects,
  clientAccounts = [],
  users,
  isSuperAdmin = false,
  fixedClientId,
  onAddProjectVersion,
  onAddProjectLabel,
  actualMinutes,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    control,
  } = useForm({
    defaultValues: {
      clientId: task?.clientId || fixedClientId || '',
      projectId: task?.projectId || '',
      title: task?.title || '',
      description: task?.description || '',
      status: (task?.status || 'todo') as TaskStatus,
      priority: (task?.priority || 'medium') as TaskPriority,
      dueDate: task?.dueDate ? format(new Date(task.dueDate), 'yyyy-MM-dd') : '',
      labels: (task?.labels || [])
        .map((l) => normalizeTaskLabel(String(l)))
        .filter((l): l is string => l != null),
      assignedTo: task?.assignedTo || [],
      githubRepoId: task?.github?.repoId ? String(task.github.repoId) : '',
      githubIssueId: task?.github?.issueId ? String(task.github.issueId) : '',
      githubIssueNumber: task?.github?.issueNumber ? String(task.github.issueNumber) : '',
      githubIssueTitle: task?.github?.issueTitle || '',
      githubIssueUrl: task?.github?.issueUrl || '',
      githubBranchName: task?.github?.branchName || '',
      githubBranchUrl: task?.github?.branchUrl || '',
      jiraIssueId: task?.jira?.issueId || '',
      jiraIssueKey: task?.jira?.issueKey || '',
      jiraIssueUrl: task?.jira?.issueUrl || '',
      version: task?.version != null ? String(task.version) : 'none',
      platform: task?.platform || 'none',
      estimatedHours: task?.estimatedHours != null ? String(task.estimatedHours) : '',
    },
    mode: 'onChange',
  });

  const { getCachedBranches, getCachedIssues } = useGithub();
  const [githubBranches, setGithubBranches] = React.useState<GithubBranch[]>([]);
  const [githubIssues, setGithubIssues] = React.useState<GithubIssue[]>([]);
  const [githubLoading, setGithubLoading] = React.useState(false);
  const [newVersionValue, setNewVersionValue] = React.useState('');
  const [versionBusy, setVersionBusy] = React.useState(false);
  const [projectVersionOverrides, setProjectVersionOverrides] = React.useState<Record<string, string[]>>({});
  const [newLabelValue, setNewLabelValue] = React.useState('');
  const [labelBusy, setLabelBusy] = React.useState(false);
  const [projectLabelOverrides, setProjectLabelOverrides] = React.useState<Record<string, string[]>>({});
  const [copyTaskIdState, setCopyTaskIdState] = React.useState<'idle' | 'copied' | 'error'>('idle');

  React.useEffect(() => {
    register('assignedTo');
    register('labels');
  }, [register]);

  const selectedClientId = watch('clientId');
  const selectedProjectId = watch('projectId');
  const selectedVersionValue = watch('version');
  const selectedGithubIssueId = watch('githubIssueId');
  const selectedGithubBranchName = watch('githubBranchName');
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId),
    [projects, selectedProjectId]
  );
  const clientGithubLinked = !!(
    selectedProject?.githubRepoId &&
    selectedProject?.githubRepoOwner &&
    selectedProject?.githubRepoName
  );
  const showGithubFields = !!(
    selectedProjectId &&
    clientGithubLinked &&
    selectedProject?.taskFieldVisibility?.github !== false
  );
  const showJiraFields = selectedProject?.taskFieldVisibility?.jira !== false;
  const selectedAssignedUserIds = watch('assignedTo') as string[] || [];
  const watchedLabels = useWatch({ control, name: 'labels', defaultValue: [] as string[] });
  const selectedLabels = useMemo(
    () => (Array.isArray(watchedLabels) ? watchedLabels : []),
    [watchedLabels]
  );
  const availableProjects = useMemo(() => {
    if (!selectedClientId) return projects;
    return projects.filter((project) => project.clientId === selectedClientId);
  }, [projects, selectedClientId]);
  const availableUsers = useMemo(() => {
    if (!selectedClientId || !selectedProjectId) return [];
    return users.filter((user) => {
      if (user.role === UserRole.SUPER_ADMIN) return true;
      if (user.clientId !== selectedClientId && user.organizationId !== selectedClientId) return false;
      if (user.role === UserRole.CLIENT_ADMIN) return true;
      if (user.projectAccess && selectedProjectId in user.projectAccess) return true;
      if (user.clientAccess && selectedClientId in user.clientAccess) return true;
      return false;
    });
  }, [users, selectedClientId, selectedProjectId]);
  const availablePlatforms = useMemo(
    () => projects.find((p) => p.id === selectedProjectId)?.platforms || [],
    [selectedProjectId, projects]
  );
  const availableVersions = useMemo(() => {
    if (!selectedProjectId) return [];
    const fromProject = projects.find((p) => p.id === selectedProjectId)?.taskVersions || [];
    const fromOverride = projectVersionOverrides[selectedProjectId] || [];
    const currentVersion = parseTaskVersion(selectedVersionValue);
    const combined = [...fromProject, ...fromOverride, ...(currentVersion ? [currentVersion] : [])]
      .map((v) => parseTaskVersion(v))
      .filter((v): v is string => v != null);
    return Array.from(new Set(combined)).sort(compareTaskVersions);
  }, [selectedProjectId, projects, projectVersionOverrides, selectedVersionValue]);

  const availableLabels = useMemo(() => {
    if (!selectedProjectId) return [];
    const fromProject = projects.find((p) => p.id === selectedProjectId)?.taskLabels || [];
    const fromOverride = projectLabelOverrides[selectedProjectId] || [];
    const combined = [
      ...DEFAULT_TASK_LABELS,
      ...fromProject,
      ...fromOverride,
      ...(selectedLabels || []),
    ]
      .map((l) => normalizeTaskLabel(String(l)))
      .filter((l): l is string => l != null);
    return Array.from(new Set(combined)).sort((a, b) => a.localeCompare(b));
  }, [selectedProjectId, projects, projectLabelOverrides, selectedLabels]);

  const toggleAssignedUser = (uid: string) => {
    const current = selectedAssignedUserIds || [];
    if (current.includes(uid)) {
      setValue('assignedTo', current.filter((id) => id !== uid), { shouldValidate: true });
      return;
    }
    setValue('assignedTo', [...current, uid], { shouldValidate: true });
  };

  const toggleLabel = (label: string) => {
    const normalized = normalizeTaskLabel(label);
    if (!normalized) return;
    const current = selectedLabels || [];
    const has = current.some((l) => normalizeTaskLabel(l) === normalized);
    if (has) {
      setValue(
        'labels',
        current.filter((l) => normalizeTaskLabel(l) !== normalized),
        { shouldValidate: true }
      );
      return;
    }
    setValue('labels', [...current, normalized], { shouldValidate: true });
  };

  const handleSubmitNewLabel = async () => {
    if (!selectedProjectId) return;
    const parsed = normalizeTaskLabel(newLabelValue);
    if (!parsed) {
      alert('Enter a short label (for example: bug, feature, docs).');
      return;
    }
    if (availableLabels.some((l) => normalizeTaskLabel(l) === parsed)) {
      const current = selectedLabels || [];
      if (!current.some((l) => normalizeTaskLabel(l) === parsed)) {
        setValue('labels', [...current, parsed], { shouldValidate: true });
      }
      setNewLabelValue('');
      return;
    }

    try {
      setLabelBusy(true);
      if (onAddProjectLabel) {
        await onAddProjectLabel(selectedProjectId, parsed);
      }
      setProjectLabelOverrides((prev) => {
        const current = prev[selectedProjectId] || [];
        return {
          ...prev,
          [selectedProjectId]: Array.from(new Set([...current, parsed])).sort((a, b) => a.localeCompare(b)),
        };
      });
      setValue('labels', [...(selectedLabels || []), parsed], { shouldValidate: true });
      setNewLabelValue('');
    } catch (error) {
      console.error('Failed to add project label:', error);
      alert('Failed to add label to this project.');
    } finally {
      setLabelBusy(false);
    }
  };

  React.useEffect(() => {
    if (!selectedProjectId || !showGithubFields) {
      setGithubBranches([]);
      setGithubIssues([]);
      return;
    }

    const loadGithubContext = async () => {
      try {
        setGithubLoading(true);
        const [branches, issues] = await Promise.all([
          getCachedBranches(selectedProjectId),
          getCachedIssues(selectedProjectId),
        ]);
        setGithubBranches(branches);
        setGithubIssues(issues);
      } catch (error) {
        console.error('Failed to load cached GitHub context:', error);
        setGithubBranches([]);
        setGithubIssues([]);
      } finally {
        setGithubLoading(false);
      }
    };

    void loadGithubContext();
  }, [selectedProjectId, showGithubFields, getCachedBranches, getCachedIssues]);

  React.useEffect(() => {
    if (!selectedGithubIssueId) {
      setValue('githubIssueNumber', '');
      setValue('githubIssueTitle', '');
      setValue('githubIssueUrl', '');
      return;
    }

    const issue = githubIssues.find((item) => String(item.id) === selectedGithubIssueId);
    if (!issue) return;

    setValue('githubIssueNumber', String(issue.number));
    setValue('githubIssueTitle', issue.title);
    setValue('githubIssueUrl', issue.htmlUrl);
  }, [selectedGithubIssueId, githubIssues, setValue]);

  React.useEffect(() => {
    if (!selectedGithubBranchName) {
      setValue('githubBranchUrl', '');
      return;
    }

    const branch = githubBranches.find((item) => item.name === selectedGithubBranchName);
    if (!branch) return;
    setValue('githubBranchUrl', branch.htmlUrl);
  }, [selectedGithubBranchName, githubBranches, setValue]);

  const handleAddVersion = async () => {
    if (!selectedProjectId) return;
    const parsed = parseTaskVersion(newVersionValue);
    if (!parsed) {
      alert('Please enter a valid version (for example: 1, 1.2, 0.15.10).');
      return;
    }
    if (availableVersions.includes(parsed)) {
      setValue('version', String(parsed), { shouldValidate: true });
      setNewVersionValue('');
      return;
    }

    try {
      setVersionBusy(true);
      if (onAddProjectVersion) {
        await onAddProjectVersion(selectedProjectId, parsed);
      }
      setProjectVersionOverrides((prev) => {
        const current = prev[selectedProjectId] || [];
        return {
          ...prev,
          [selectedProjectId]: Array.from(new Set([...current, parsed])).sort(compareTaskVersions),
        };
      });
      setValue('version', String(parsed), { shouldValidate: true });
      setNewVersionValue('');
    } catch (error) {
      console.error('Failed to add project version:', error);
      alert('Failed to add version to this project.');
    } finally {
      setVersionBusy(false);
    }
  };

  const formatMinutes = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  };

  const handleCopyTaskId = async () => {
    if (!task?.id) return;
    try {
      await navigator.clipboard.writeText(task.id);
      setCopyTaskIdState('copied');
      window.setTimeout(() => setCopyTaskIdState('idle'), 1800);
    } catch (error) {
      console.error('Failed to copy task ID:', error);
      setCopyTaskIdState('error');
      window.setTimeout(() => setCopyTaskIdState('idle'), 2200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[min(90dvh,calc(100vh-2rem))] w-full max-w-[min(56rem,calc(100vw-2rem))] flex-col gap-4 overflow-hidden p-6">
        <DialogHeader className="shrink-0">
          <DialogTitle>{task ? 'Edit Task' : 'Create Task'}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden"
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden px-1.5">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {isSuperAdmin ? (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Client <span className="text-error">*</span>
                  </label>
                  <Select
                    value={watch('clientId')}
                    onValueChange={(value) => {
                      setValue('clientId', value, { shouldValidate: true });
                      setValue('projectId', '', { shouldValidate: true });
                      setValue('assignedTo', [], { shouldValidate: true });
                      setValue('labels', [], { shouldValidate: true });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientAccounts.map((clientAccount) => (
                        <SelectItem key={clientAccount.id} value={clientAccount.id}>
                          {clientAccount.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <input
                    type="hidden"
                    {...register('clientId', { required: 'Client is required' })}
                  />
                  {errors.clientId && (
                    <p className="text-sm text-error">{errors.clientId.message as string}</p>
                  )}
                </div>
              ) : (
                <Input
                  label="Client Account ID"
                  {...register('clientId', { required: 'Client is required' })}
                  disabled={!!fixedClientId}
                  error={errors.clientId?.message}
                />
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Project <span className="text-error">*</span>
                </label>
                <Select
                  value={watch('projectId')}
                  onValueChange={(value) => {
                    setValue('projectId', value, { shouldValidate: true });
                    setValue('assignedTo', [], { shouldValidate: true });
                    setValue('labels', [], { shouldValidate: true });
                    setValue('githubRepoId', '');
                    setValue('githubIssueId', '');
                    setValue('githubIssueNumber', '');
                    setValue('githubIssueTitle', '');
                    setValue('githubIssueUrl', '');
                    setValue('githubBranchName', '');
                    setValue('githubBranchUrl', '');
                    setValue('jiraIssueId', '');
                    setValue('jiraIssueKey', '');
                    setValue('jiraIssueUrl', '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProjects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <input
                  type="hidden"
                  {...register('projectId', { required: 'Project is required' })}
                />
                {errors.projectId && (
                  <p className="text-sm text-error">{errors.projectId.message as string}</p>
                )}
              </div>
            </div>

            <Input
              label="Task Title"
              {...register('title', { required: 'Title is required' })}
              error={errors.title?.message}
            />
            <Textarea
              label="Description"
              {...register('description')}
              rows={4}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={watch('status')}
                  onValueChange={(value) => setValue('status', value as TaskStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in-progress">In Progress</SelectItem>
                    <SelectItem value="in-review">In Review</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="done">Done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select
                  value={watch('priority')}
                  onValueChange={(value) => setValue('priority', value as TaskPriority)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Assignee</label>
                {!watch('projectId') ? (
                  <p className="text-sm text-muted-foreground">Select a project to choose users.</p>
                ) : availableUsers.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No users available for this client/project.</p>
                ) : (
                  <div className="max-h-44 overflow-y-auto rounded-md border p-3 space-y-2">
                    {availableUsers.map((user) => {
                      const userLabel = user.displayName ||
                        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
                        user.email ||
                        user.uid;
                      const checked = selectedAssignedUserIds.includes(user.uid);
                      return (
                        <label key={user.uid} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleAssignedUser(user.uid)}
                          />
                          <span className="text-sm">{userLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {availablePlatforms.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Platform (optional)</label>
                  <Select
                    value={watch('platform')}
                    onValueChange={(value) => setValue('platform', value, { shouldValidate: true })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {availablePlatforms.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium">Version (optional)</label>
                <Select
                  value={watch('version')}
                  onValueChange={(value) => setValue('version', value, { shouldValidate: true })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {availableVersions.map((v) => (
                      <SelectItem key={v} value={String(v)}>
                        {formatTaskVersion(v)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Add version (e.g. 0.15.10)"
                    value={newVersionValue}
                    onChange={(event) => setNewVersionValue(event.target.value)}
                    disabled={!selectedProjectId || versionBusy}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddVersion}
                    disabled={!selectedProjectId || versionBusy || !newVersionValue.trim()}
                  >
                    Add
                  </Button>
                </div>
              </div>
              <Input
                label="Due Date"
                type="date"
                {...register('dueDate')}
              />
              <Input
                label="Estimated Hours"
                type="number"
                step="0.25"
                min="0"
                placeholder="e.g. 12.5"
                {...register('estimatedHours')}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Labels</label>
              {!selectedProjectId ? (
                <p className="text-sm text-muted-foreground">Select a project to choose labels.</p>
              ) : (
                <div className="flex flex-wrap gap-x-4 gap-y-2 rounded-md border p-3">
                  {availableLabels.map((label) => {
                    const checked = (selectedLabels || []).some(
                      (l) => normalizeTaskLabel(l) === normalizeTaskLabel(label)
                    );
                    return (
                      <label key={label} className="flex cursor-pointer items-center gap-2">
                        <Checkbox checked={checked} onCheckedChange={() => toggleLabel(label)} />
                        <span className="text-sm capitalize">{label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="flex max-w-md items-center gap-2">
                <Input
                  placeholder="Add label (e.g. docs, regression)"
                  value={newLabelValue}
                  onChange={(event) => setNewLabelValue(event.target.value)}
                  disabled={!selectedProjectId || labelBusy}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleSubmitNewLabel();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleSubmitNewLabel()}
                  disabled={!selectedProjectId || labelBusy || !newLabelValue.trim()}
                >
                  Add
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Default options include bug and feature. New labels are saved on this project for future tasks.
              </p>
            </div>

            {task?.id && (
              <div className="space-y-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium">Task Number: </span>
                    <span className="font-mono">{task.id}</span>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => void handleCopyTaskId()}>
                    {copyTaskIdState === 'copied' ? (
                      <>
                        <Check className="h-4 w-4" />
                        Copied ID
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4" />
                        Copy ID
                      </>
                    )}
                  </Button>
                </div>
                <div>
                  <span className="font-medium">Actual Time: </span>
                  <span>{(actualMinutes || 0) > 0 ? formatMinutes(actualMinutes || 0) : '—'}</span>
                </div>
                {copyTaskIdState === 'error' && (
                  <p className="text-xs text-error">Could not copy task ID. Please copy it manually.</p>
                )}
              </div>
            )}
            {showGithubFields && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-medium">GitHub</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">GitHub Issue (Optional)</label>
                    <Select
                      value={watch('githubIssueId')}
                      onValueChange={(value) => {
                        setValue('githubIssueId', value);
                        const issue = githubIssues.find((item) => String(item.id) === value);
                        if (issue) {
                          setValue('githubRepoId', issue.repoId ? String(issue.repoId) : '');
                          setValue('githubIssueNumber', String(issue.number));
                          setValue('githubIssueTitle', issue.title);
                          setValue('githubIssueUrl', issue.htmlUrl);
                        }
                      }}
                      disabled={!selectedProjectId || githubLoading || githubIssues.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={githubLoading ? 'Loading cached issues...' : 'Select linked issue'} />
                      </SelectTrigger>
                      <SelectContent>
                        {githubIssues.map((issue) => (
                          <SelectItem key={issue.id} value={String(issue.id)}>
                            #{issue.number} {issue.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">GitHub Branch (Optional)</label>
                    <Select
                      value={watch('githubBranchName')}
                      onValueChange={(value) => setValue('githubBranchName', value)}
                      disabled={!selectedProjectId || githubLoading || githubBranches.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={githubLoading ? 'Loading cached branches...' : 'Select linked branch'} />
                      </SelectTrigger>
                      <SelectContent>
                        {githubBranches.map((branch) => (
                          <SelectItem key={branch.name} value={branch.name}>
                            {branch.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Lists use cached GitHub data; run a repo sync from the project&apos;s GitHub settings if pickers are empty.
                </p>
                {selectedProjectId && !clientGithubLinked && (
                  <p className="text-xs text-muted-foreground">
                    No GitHub repository linked for this project. Link one on the project record to load issues and branches.
                  </p>
                )}
              </div>
            )}

            {showJiraFields && (
              <div className="space-y-3 border-t pt-4">
                <p className="text-sm font-medium">Jira</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="Jira Issue ID"
                    placeholder="Optional"
                    {...register('jiraIssueId')}
                  />
                  <Input
                    label="Jira Issue Key"
                    placeholder="e.g. TAM-123"
                    {...register('jiraIssueKey')}
                  />
                  <Input
                    label="Jira Issue URL"
                    placeholder="https://..."
                    {...register('jiraIssueUrl')}
                  />
                </div>
              </div>
            )}
          </div>
          {task?.id && <div className="border-t py-5 space-y-5"><WorkApproval task={{ ...task, id: task.id }} staff={isSuperAdmin} /><Conversation parentId={task.id} kind="task" /></div>}
          <DialogFooter className="shrink-0 border-t border-gray-100 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting}>
              {task ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
